<h1 mat-dialog-title>Send Personalized Notification Emails</h1>

<mat-dialog-content>
  <div *ngIf="loading" class="loading-container">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading preview...</p>
  </div>

  <div *ngIf="!loading">
    <!-- Important Notice -->
    <div class="alert alert-info mb-3">
      <i class="bi bi-info-circle me-2"></i>
      <strong>Personalized Emails:</strong> Each user will receive their own email with only their subscribed portfolios.
      User A gets their watchlists, User B gets their watchlists - no mixing.
    </div>

    <!-- User Summary -->
    <div class="user-summary mb-3">
      <div class="alert" [class.alert-success]="getUsersWithPortfoliosCount() > 0" [class.alert-warning]="getUsersWithPortfoliosCount() === 0">
        <strong>Recipients:</strong> {{ getUsersWithPortfoliosCount() }} of {{ eligibleUsers.length }} eligible user(s) will receive emails
        <span *ngIf="getUsersWithPortfoliosCount() !== eligibleUsers.length" class="text-muted ms-2">
          ({{ eligibleUsers.length - getUsersWithPortfoliosCount() }} have no portfolios to send)
        </span>
        <span *ngIf="filteredOutUsers.length > 0" class="text-warning ms-2">
          ({{ filteredOutUsers.length }} user(s) filtered out)
        </span>
      </div>

      <!-- Eligible Users with their portfolios -->
      <div *ngIf="eligibleUsers.length > 0" class="mb-3">
        <details>
          <summary class="cursor-pointer"><strong>User Portfolio Summary ({{ eligibleUsers.length }})</strong></summary>
          <ul class="list-group mt-2" style="max-height: 200px; overflow-y: auto;">
            <li *ngFor="let user of eligibleUsers" class="list-group-item py-2">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <strong>{{ user.username }}</strong> ({{ user.organisation }})
                  <br>
                  <small class="text-muted">{{ user.email }}</small>
                </div>
                <div class="text-end">
                  <span *ngIf="user.portfolio_count > 0" class="badge bg-success">
                    {{ user.portfolio_count }} portfolio(s)
                  </span>
                  <span *ngIf="user.portfolio_count === 0" class="badge bg-warning text-dark">
                    No portfolios
                  </span>
                </div>
              </div>
              <!-- Show user's portfolios -->
              <div *ngIf="user.portfolios && user.portfolios.length > 0" class="mt-2">
                <small class="text-muted">Will receive:</small>
                <div class="d-flex flex-wrap gap-1 mt-1">
                  <span *ngFor="let p of user.portfolios"
                        class="badge"
                        [class.bg-primary]="p.type === 'watchlist'"
                        [class.bg-secondary]="p.type === 'benchmark'">
                    {{ p.type === 'watchlist' ? '📁' : '📊' }} {{ p.name }}
                  </span>
                </div>
              </div>
            </li>
          </ul>
        </details>
      </div>

      <!-- Filtered Out Users -->
      <div *ngIf="filteredOutUsers.length > 0" class="mb-3">
        <details>
          <summary class="cursor-pointer text-warning"><strong>Filtered Out ({{ filteredOutUsers.length }})</strong></summary>
          <ul class="list-group mt-2" style="max-height: 100px; overflow-y: auto;">
            <li *ngFor="let user of filteredOutUsers" class="list-group-item d-flex justify-content-between align-items-center py-1 list-group-item-warning">
              <span>{{ user.username }}</span>
              <span class="badge bg-warning text-dark">{{ user.reason }}</span>
            </li>
          </ul>
        </details>
      </div>
    </div>

    <!-- Portfolio Selection Options -->
    <div class="portfolio-options mb-3">
      <strong>What to Include in Emails:</strong>

      <!-- Include Watchlists Toggle -->
      <div class="form-check mt-2">
        <input class="form-check-input"
               type="checkbox"
               id="includeWatchlists"
               [(ngModel)]="includeWatchlists">
        <label class="form-check-label" for="includeWatchlists">
          <i class="bi bi-folder me-1"></i>
          Include user's own Watchlists
          <small class="text-muted">(each user gets only their own watchlists)</small>
        </label>
      </div>

      <!-- Benchmark Selection -->
      <div *ngIf="availableBenchmarks.length > 0" class="mt-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <span>
            <i class="bi bi-graph-up me-1"></i>
            <strong>Benchmarks</strong>
            <small class="text-muted">({{ getSelectedBenchmarkCount() }} of {{ availableBenchmarks.length }} selected)</small>
          </span>
          <div class="btn-group btn-group-sm">
            <button type="button" class="btn btn-outline-secondary btn-sm" (click)="selectAllBenchmarks()">All</button>
            <button type="button" class="btn btn-outline-secondary btn-sm" (click)="deselectAllBenchmarks()">None</button>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6" *ngFor="let benchmark of availableBenchmarks">
            <div class="form-check">
              <input class="form-check-input"
                     type="checkbox"
                     [id]="'benchmark-' + benchmark.id"
                     [checked]="benchmark.selected"
                     (change)="toggleBenchmark(benchmark)">
              <label class="form-check-label" [for]="'benchmark-' + benchmark.id">
                {{ benchmark.name }}
                <span class="badge bg-secondary ms-1">{{ benchmark.company_count }} companies</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Regenerate Button -->
      <div class="mt-3">
        <button type="button"
                class="btn btn-outline-primary btn-sm"
                (click)="regeneratePreview()"
                [disabled]="regenerating || (!includeWatchlists && getSelectedBenchmarkCount() === 0)">
          <span *ngIf="regenerating">
            <span class="spinner-border spinner-border-sm me-1"></span>
            Updating Preview...
          </span>
          <span *ngIf="!regenerating">
            <i class="bi bi-arrow-clockwise me-1"></i>
            Update Preview
          </span>
        </button>
        <small *ngIf="!includeWatchlists && getSelectedBenchmarkCount() === 0" class="text-danger ms-2">
          Select at least watchlists or one benchmark
        </small>
      </div>
    </div>

    <!-- Email Form -->
    <form [formGroup]="emailForm">
      <div class="mb-3">
        <label for="subject" class="form-label"><strong>Subject:</strong></label>
        <input type="text" class="form-control" id="subject" formControlName="subject" placeholder="Email subject" />
      </div>

      <mat-tab-group>
        <!-- Preview Tab -->
        <mat-tab label="Email Preview">
          <div class="mt-3">
            <div *ngIf="previewNote" class="alert alert-secondary py-2 mb-2">
              <small><i class="bi bi-eye me-1"></i> {{ previewNote }}</small>
            </div>
            <div class="email-preview-container">
              <div class="email-preview" [innerHTML]="previewHtml"></div>
            </div>
          </div>
        </mat-tab>
      </mat-tab-group>
    </form>
  </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button type="button" class="btn btn-secondary me-2" (click)="onCancel()" [disabled]="sending">
    Cancel
  </button>
  <button
    type="button"
    class="btn btn-primary"
    (click)="onSend()"
    [disabled]="emailForm.invalid || sending || getUsersWithPortfoliosCount() === 0 || (!includeWatchlists && getSelectedBenchmarkCount() === 0)"
  >
    <span *ngIf="sending">
      <span class="spinner-border spinner-border-sm me-1" role="status"></span>
      Sending...
    </span>
    <span *ngIf="!sending">
      Send to {{ getUsersWithPortfoliosCount() }} User(s)
    </span>
  </button>
</mat-dialog-actions>
